import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Input,
  inject,
  ElementRef,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonIcon,
  IonThumbnail,
  IonImg,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { pin, removeCircle } from 'ionicons/icons';
import { APP_SETTINGS_TOKEN, InfoPinService } from '@crowdctrl/shared';
import { Session } from '@crowdctrl/core';
import { SessionContextService } from '../../../../../../services/session-context.service';
import { Subscription, combineLatest } from 'rxjs';
import { startWith } from 'rxjs/operators';

addIcons({ pin, removeCircle });

@Component({
  selector: 'app-session-info',
  standalone: true,
  imports: [
    CommonModule,
    IonCard,
    IonCardHeader,
    IonCardTitle,
    IonCardSubtitle,
    IonButton,
    IonIcon,
    IonThumbnail,
    IonImg,
  ],
  templateUrl: './session-info.component.html',
  styleUrl: './session-info.component.css',
})
export class SessionInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  private sessionContextService = inject(SessionContextService);
  private appSettings = inject(APP_SETTINGS_TOKEN);
  private infoPinService = inject(InfoPinService);
  private elementRef = inject(ElementRef);

  /**
   * When false, this route does not host session info: no in-page card and no header projection.
   * (Pin preference in storage is unchanged; it only applies again when a normal instance mounts.)
   */
  @Input() showInfoCard = true;

  @ViewChild('infoCardTpl') infoCardTpl?: TemplateRef<unknown>;

  session$ = this.sessionContextService.session$;
  sessionId$ = this.sessionContextService.sessionId$;
  settings$ = this.appSettings.settings$;
  isPinned$ = this.infoPinService
    .pinnedState$('session')
    .pipe(startWith(this.infoPinService.isPinned('session')));

  private subscriptions = new Subscription();
  private scrollTimeout: ReturnType<typeof setTimeout> | undefined;
  private scrollTeardown?: () => void;
  private destroyed = false;
  isScrolling = false;

  /**
   * When true, binds `.scrolling` while `ion-content` scrolls so CSS can dim the pinned card.
   * Disabled for now; scroll listener still runs.
   */
  readonly pinnedInfoScrollTransparencyEffect = false;

  constructor() {
    const current = this.infoPinService.getPinnedContent();
    if (current && current.type !== 'session') {
      this.infoPinService.setPinnedContent(current.type, null);
    }
  }

  ngOnInit(): void {
    if (!this.showInfoCard) {
      this.clearSessionPinnedShellTemplate();
    }
  }

  ngAfterViewInit(): void {
    if (!this.showInfoCard) {
      return;
    }
    this.subscriptions.add(
      combineLatest([this.session$, this.settings$, this.isPinned$]).subscribe(
        ([session, settings, pinned]) => {
          this.applyPinnedShellState(session, settings, pinned, 0);
        },
      ),
    );
  }

  /**
   * Pinned layout registers `infoCardTpl` with InfoPinService. `ViewChild` can lag the first
   * `combineLatest` emission after route swaps; without a deferred retry the toolbar stays empty
   * while the in-page branch stays hidden (pinned).
   */
  private applyPinnedShellState(
    session: Session | null,
    settings: unknown,
    pinned: boolean,
    tplRetry: number,
  ): void {
    if (this.destroyed) {
      return;
    }
    if (!this.infoCardTpl) {
      if (tplRetry < 50) {
        queueMicrotask(() => this.applyPinnedShellState(session, settings, pinned, tplRetry + 1));
      }
      return;
    }

    if (pinned && session && settings) {
      this.infoPinService.setPinnedContent('session', this.infoCardTpl, {
        session,
        settings,
      });
      this.setupScrollListener();
    } else {
      this.removeScrollListener();
      if (!pinned) {
        const cur = this.infoPinService.getPinnedContent();
        if (cur?.type === 'session') {
          this.infoPinService.setPinnedContent('session', null);
        }
      }
    }
  }

  private clearSessionPinnedShellTemplate(): void {
    const cur = this.infoPinService.getPinnedContent();
    if (cur?.type === 'session') {
      this.infoPinService.setPinnedContent('session', null);
    }
  }

  private setupScrollListener(): void {
    this.removeScrollListener();
    const ionContent = this.elementRef.nativeElement.closest('ion-content');
    if (!ionContent) return;

    void ionContent.getScrollElement().then((scrollEl: HTMLElement) => {
      const handleScroll = () => {
        this.isScrolling = true;
        if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.isScrolling = false;
        }, 150);
      };
      scrollEl.addEventListener('scroll', handleScroll, { passive: true });
      this.scrollTeardown = () => scrollEl.removeEventListener('scroll', handleScroll);
    });
  }

  private removeScrollListener(): void {
    this.isScrolling = false;
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = undefined;
    }
    this.scrollTeardown?.();
    this.scrollTeardown = undefined;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    // Do not clear InfoPinService here: pin state is global; clearing on destroy blanks the next
    // sibling route's header until it re-registers. session-tabs / workspace-tabs clear when
    // leaving session/workspace scope; the next mounted SessionInfo replaces the template when pinned.
    this.removeScrollListener();
    this.subscriptions.unsubscribe();
  }

  /** Toggle header vs in-page layout for this session card (not show on all pages). */
  togglePin(): void {
    this.infoPinService.togglePinned('session');
  }
}
